import React, { useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import {
  Add,
  Remove,
  Delete,
  WarningAmber,
  Close,
  WhatsApp,
} from "@mui/icons-material";

export default function ShopPage() {
  const { cartItems, setCartItems, clearCart } = useContext(AppContext);
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(true);

  const handleQuantityChange = (index, delta) => {
    const updated = [...cartItems];
    updated[index].quantity = Math.max(1, updated[index].quantity + delta);
    setCartItems(updated);
  };

  const handleRemoveItem = (index) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  const handleGoToProduct = (item) => {
    if (item.variantUrl) navigate(item.variantUrl);
    else navigate(`/categorias/${item.section}/${item.category}/${item.slug || item.name}`);
  };

  const calculateTotal = () =>
    cartItems.reduce(
      (sum, item) => sum + Number(item.price_with_iva ?? 0) * item.quantity,
      0
    );

  const generateWhatsAppMessage = () => {
    const base = "https://drsolucionesinformaticas.com";
    let msg = "Hola, estoy interesado en:%0A%0A";

    cartItems.forEach((it) => {
      const url = it.variantUrl
        ? `${base}${it.variantUrl}`
        : `${base}/categorias/${it.section}/${it.category}/${it.slug || it.name}`;

      const price = Number(it.price_with_iva ?? 0).toFixed(2);

      msg += `• ${it.name}%0A`;
      if (it.variant) msg += `  Variante: ${it.variant.type} – ${it.variant.value}%0A`;
      if (it.sku) msg += `  SKU: ${it.sku}%0A`;
      msg += `  Precio: $${price}%0A`;
      msg += `  Cantidad: ${it.quantity}%0A`;
      msg += `  URL: ${url}%0A%0A`;
    });

    msg += `Total: $${calculateTotal().toFixed(2)}%0A`;

    return `https://wa.me/593984493776?text=${msg}`;
  };

  const handleSendWhatsApp = () => {
    window.open(generateWhatsAppMessage(), "_blank");
  };

  return (
    <Box sx={{ p: 4, width: "80%", mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Detalles de Compra
      </Typography>

      {showAlert && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            border: "1px solid #ffb74d",
            backgroundColor: "#fff3e0",
            borderRadius: 2,
            display: "flex",
            position: "relative",
          }}
        >
          <WarningAmber sx={{ color: "#ff9800", mr: 1, mt: "3px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", color: "#333" }}>
              El cliente es responsable por entender y averiguar sobre la descripción del producto.
            </Typography>
            <Typography sx={{ color: "#333" }}>
              No nos hacemos responsables si realizas una compra sin leer detalladamente la descripción.{" "}
              <Button
                onClick={() => navigate("/terminos-condiciones")}
                variant="text"
                sx={{
                  color: "#0A3D91",
                  textTransform: "none",
                  fontWeight: "bold",
                  p: 0,
                  minWidth: 0,
                }}
              >
                Ver más en TYC
              </Button>
            </Typography>
          </Box>
          <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowAlert(false)}>
            <Close />
          </IconButton>
        </Box>
      )}

      {cartItems.length === 0 ? (
        <Typography variant="h6" color="text.secondary">
          Tu carrito está vacío.
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {cartItems.map((item, index) => {
              const price = Number(item.price_with_iva ?? 0).toFixed(2);
              return (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <img
                      src={item.image || item.images?.[0] || ""}
                      alt={item.name}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        borderRadius: 5,
                        marginRight: 16,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontWeight: "bold", cursor: "pointer", color: "#000" }}
                        onClick={() => handleGoToProduct(item)}
                      >
                        {item.name}
                      </Typography>

                      {item.variant && (
                        <Typography sx={{ color: "#666" }}>
                          {item.variant.type}: {item.variant.value}
                        </Typography>
                      )}

                      <Typography sx={{ color: "#666" }}>REF: {item.code}</Typography>

                      {item.sku && (
                        <Typography sx={{ color: "#888" }}>SKU: {item.sku}</Typography>
                      )}

                      <Typography sx={{ color: "#1565C0", fontWeight: "bold", mt: 0.5 }}>
                        $ {price}
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                        <IconButton onClick={() => handleQuantityChange(index, -1)} size="small" sx={{ bgcolor: "#f0f0f0" }}>
                          <Remove />
                        </IconButton>
                        <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton onClick={() => handleQuantityChange(index, 1)} size="small" sx={{ bgcolor: "#f0f0f0" }}>
                          <Add />
                        </IconButton>

                        <IconButton onClick={() => handleRemoveItem(index)} sx={{ color: "#e53935", ml: 2 }}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Typography variant="h6">Total a pagar</Typography>
            <Typography variant="h6">$ {calculateTotal().toFixed(2)}</Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={clearCart}
              sx={{
                color: "#0A3D91",
                borderColor: "#0A3D91",
                "&:hover": { bgcolor: "#0A3D9120" },
              }}
            >
              VACIAR CARRITO
            </Button>

            <Button
              variant="contained"
              sx={{
                bgcolor: "#0A3D91",
                color: "#fff",
                fontWeight: "bold",
              }}
              onClick={() => navigate("/checkout")}
            >
              IR A PAGAR
            </Button>
          </Box>

          <Box
            sx={{
              mt: 3,
              p: 1.5,
              bgcolor: "#fff8e1",
              border: "1px solid #ffe082",
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "#7a5b00", fontWeight: "bold" }}>
              Si tienes alguna duda o necesitas asesoría,
              puedes realizar tu compra directamente por WhatsApp.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<WhatsApp />}
              onClick={handleSendWhatsApp}
              sx={{
                bgcolor: "#25D366",
                color: "#fff",
                px: 3,
                fontWeight: "bold",
                "&:hover": { bgcolor: "#1ebe5d" },
              }}
            >
              COMPRAR POR WHATSAPP
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              variant="text"
              onClick={() => navigate("/categorias")}
              sx={{
                color: "#0A3D91",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Seguir Comprando
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
